import PropTypes from "prop-types";
import React, { useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  ButtonBase,
  CardContent,
  ClickAwayListener,
  Grid,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
  Avatar,
  Chip
} from "@mui/material";
import { KeyOutlined } from "@ant-design/icons";
import { dispatch } from "store/index";
import CircularProgress from "@mui/material/CircularProgress";
import { AES, enc } from "crypto-js";
import * as url from "../../../../../store/constant/Endpoints";
import { ChangePassword } from "store/action/index";

// project-imports
import MainCard from "components/MainCard";
import Transitions from "components/@extended/Transitions";
import { Button, Modal, Space } from "antd";
import IconButton from "components/@extended/IconButton";
import PasswordInput from "components/inputs/PasswordInput";
import SIDEBAR from "../../../Drawer/sidebarStyles";

// assets
import { Logout, User } from "iconsax-react";

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const ProfilePage = () => {
  const theme = useTheme();

  const handleLogout = async () => {
    localStorage.clear();
    window.location.reload();
  };

  const [loading, setLoading] = useState(false);
  const [current_password, setcurrentPasssword] = useState("");
  const [password, setnewPassword] = useState("");
  const [confirmPassword, setconfirmPassword] = useState("");

  const [fieldErrors, setFieldErrors] = useState({
    current_password: "",
    password: "",
    confirmPassword: "",
    password_error_text: "",
    current_password_error_text: "",
    c_password_error_text: "",
  });

  let permissionUser = localStorage.getItem("data");
  let decryptedData = null;
  if (permissionUser) {
    try {
      decryptedData = AES.decrypt(permissionUser, url.encryptionSecret).toString(enc.Utf8);
    } catch (error) {
      console.log(error, "er");
    }
  }

  const loginDetails = decryptedData ? JSON.parse(decryptedData) : null;

  const setPasswordValidation = (value, newPass, confirmNewPass) => {
    const newFieldErrors = { ...fieldErrors };

    if (value === "newPass") {
      if (newPass.length < 6) {
        newFieldErrors.password = true;
        newFieldErrors.password_error_text = "Password must be at least 6 characters";
      } else {
        newFieldErrors.password = false;
        newFieldErrors.password_error_text = "";
      }
    } else if (value === "confirmNewPass") {
      if (password !== confirmNewPass) {
        newFieldErrors.confirmPassword = true;
        newFieldErrors.c_password_error_text = "Password does not match";
      } else {
        newFieldErrors.confirmPassword = false;
        newFieldErrors.c_password_error_text = "";
      }
    }

    setFieldErrors(newFieldErrors);
  };

  const handleBlur = () => {
    setPasswordValidation();
  };

  const handleOk = () => {
    setLoading(true);
    setPasswordValidation();

    if (!fieldErrors?.confirmPassword) {
      dispatch(
        ChangePassword({
          current_password: current_password,
          password: password,
          default_password: 1,
          setModalOpen: setModalOpen,
          setLoading: setLoading,
        })
      );
    }
  };

  const handleCancel = () => {
    setModalOpen(false);
  };

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  const handleChange = () => {
    setModalOpen(true);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const userName = loginDetails?.name || "Admin User";
  const userEmail = loginDetails?.email || "admin@qrcode.com";
  const userInitials = userName
    ? userName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "AU";

  return (
    <Box sx={{ flexShrink: 0, ml: 1 }}>
      <ButtonBase
        sx={{
          p: 0.5,
          px: 1,
          borderRadius: 2,
          bgcolor: SIDEBAR.hoverBg,
          border: `1px solid ${SIDEBAR.activeBorder}`,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            bgcolor: SIDEBAR.activeBg,
            borderColor: SIDEBAR.textActive,
          },
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: "primary.main",
              color: "#ffffff",
              fontSize: "0.85rem",
              fontWeight: 700,
              boxShadow: "0 2px 8px rgba(37, 99, 235, 0.3)",
            }}
          >
            {userInitials}
          </Avatar>
          <Box sx={{ textAlign: "left", display: { xs: "none", sm: "block" } }}>
            <Typography variant="subtitle2" sx={{ color: "#f8fafc", fontWeight: 600, leading: 1.2 }}>
              {userName}
            </Typography>
            <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", fontSize: "0.725rem" }}>
              Administrator
            </Typography>
          </Box>
        </Stack>
      </ButtonBase>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 10],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                width: 290,
                borderRadius: 2.5,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard sx={{ border: "none" }} content={false}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor: "primary.main",
                            color: "#ffffff",
                            fontSize: "1.1rem",
                            fontWeight: 700,
                          }}
                        >
                          {userInitials}
                        </Avatar>
                        <Stack spacing={0.25} sx={{ overflow: "hidden" }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                            {userName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                            {userEmail}
                          </Typography>
                          <Chip label="Admin Access" color="primary" size="small" variant="light" sx={{ height: 20, fontSize: "0.65rem", mt: 0.5, width: "fit-content" }} />
                        </Stack>
                      </Stack>

                      <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2, display: "flex", gap: 1 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="secondary"
                          size="small"
                          startIcon={<KeyOutlined />}
                          onClick={handleChange}
                          sx={{ borderRadius: 1.5, textTransform: "none", py: 0.75 }}
                        >
                          Password
                        </Button>
                        <Button
                          fullWidth
                          variant="contained"
                          color="error"
                          size="small"
                          startIcon={<Logout size="16" />}
                          onClick={handleLogout}
                          sx={{ borderRadius: 1.5, textTransform: "none", py: 0.75 }}
                        >
                          Logout
                        </Button>
                      </Box>
                    </Stack>
                  </CardContent>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      <Modal
        open={isModalOpen}
        title="Change Password"
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" style={{ backgroundColor: "#D50F2B" }} type="primary" onClick={handleOk}>
            {loading ? <CircularProgress size={14} color="inherit" /> : "Change Password"}
          </Button>,
        ]}
      >
        <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <PasswordInput
            value={current_password}
            onBlur={handleBlur}
            onChange={(e) => setcurrentPasssword(e.target.value)}
            type="password"
            name="Current Password*"
            placeholder="Current Password*"
            error={fieldErrors.current_password}
            errorText={fieldErrors.current_password_error_text}
          />
          <PasswordInput
            value={password}
            onBlur={handleBlur}
            onChange={(e) => {
              setnewPassword(e.target.value);
              setPasswordValidation("newPass", e.target.value, "");
            }}
            type="password"
            name="Password*"
            placeholder="Password*"
            error={fieldErrors.password}
            errorText={fieldErrors.password_error_text}
          />
          <PasswordInput
            value={confirmPassword}
            onBlur={handleBlur}
            name="Confirm Password*"
            placeholder="Confirm Password"
            onChange={(e) => {
              setconfirmPassword(e.target.value);
              setPasswordValidation("confirmNewPass", "", e.target.value);
            }}
            error={fieldErrors.confirmPassword}
            errorText={fieldErrors.c_password_error_text}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default ProfilePage;
